import { ARTBOARD_PRESETS, type PresetInfo } from '../editor/artboardPresets';

/**
 * What a new file starts as, from the Home: a size, and the words it was started from.
 *
 * The sizes are the editor's own presets (one list, every consumer), and six of them are
 * the ones offered under the prompt box — the shapes people start from most.
 */

/** The first board of a new file, in document units (points). */
export interface StartSize {
  readonly width: number;
  readonly height: number;
  /** What the board is called: the preset's name, or "Custom" with its numbers. */
  readonly name: string;
  /** The preset it came from, stored on the board. */
  readonly preset?: string;
  /**
   * The unit the file is thought in: a paper size's own (mm, in), so an A4 reads 210 × 297
   * mm and not 595 × 842, its size in points (QA 2026-10-05, ART-HOM-10). Absent: pixels.
   */
  readonly units?: 'mm' | 'in';
}

const QUICK = ['post', 'portrait-post', 'story', 'poster', 'a4', 'slide'] as const;

/** The sizes under the prompt box, in the order they are offered. */
export const QUICK_SIZES: readonly PresetInfo[] = QUICK.map((id) => ARTBOARD_PRESETS.find((preset) => preset.id === id)!);

/** A preset as the Home names it: short, because it sits in a small pill. */
export const shortName = (preset: PresetInfo): string => (preset.id === 'slide' ? 'Slide' : preset.name);

/** How a size is named in its pill: the preset's short name, or its numbers. */
export function sizeLabel(size: StartSize): string {
  const preset = size.preset ? ARTBOARD_PRESETS.find((one) => one.id === size.preset) : undefined;
  return preset ? shortName(preset) : size.name;
}

/** The unit a preset is stated in, when it is not pixels. */
export function unitsOf(preset: PresetInfo): StartSize['units'] {
  return preset.says.endsWith(' mm') ? 'mm' : preset.says.endsWith(' in') ? 'in' : undefined;
}

export const startSizeOf = (preset: PresetInfo): StartSize => {
  const units = unitsOf(preset);
  // A paper size keeps its exact points: rounded, an A4 read 209.9 × 297.04 mm.
  return {
    width: units ? preset.width : Math.round(preset.width),
    height: units ? preset.height : Math.round(preset.height),
    name: preset.name,
    preset: preset.id,
    ...(units ? { units } : {}),
  };
};

/** A size somebody typed, in pixels, as a board. */
export function customSize(width: number, height: number): StartSize {
  const w = Math.round(Math.min(Math.max(width, 1), 100_000));
  const h = Math.round(Math.min(Math.max(height, 1), 100_000));
  return { width: w, height: h, name: `${w} × ${h}` };
}

/** The longest name a file takes from the words it was started from. */
const LONGEST_NAME = 120;

/**
 * A file started from words is named by them, until there is something that can do more
 * with them: the first line that says anything, spaces tidied, cut at a word where it can
 * be. Nothing typed is "Untitled", as a New File always was.
 */
export function fileNameFrom(words: string): string {
  const line = words.split('\n').map((one) => one.replace(/\s+/g, ' ').trim()).find((one) => one !== '') ?? '';
  if (line === '') return 'Untitled';
  if (line.length <= LONGEST_NAME) return line;
  const cut = line.slice(0, LONGEST_NAME);
  const space = cut.lastIndexOf(' ');
  return `${(space > LONGEST_NAME * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}
